<template>
  <cs-preloader-overlay :loading="loading">
    <div id="warp" class="wide">
      <payment-header
        :title="displayTitle"
        :language="getLanguage"
        has-redirect
        @redirect-back="tryAgain"
      />

      <div id="rename-me-02">
        <div class="information-card-container">
          <div
            class="card"
            :class="[
              flipped && 'flipped',
              inputActive && 'active',
              classBankType,
              classBankName,
            ]"
          >
            <div>
              <div class="front">
                <label for="">
                  <p v-show="panIsValidClass">
                    <span>
                      <b>{{ cardPan }}</b> {{ getCardPlaceholders('cardPan', 18) }}
                    </span>
                  </p>
                  <p v-show="!panIsValidClass">
                    <span>{{ displayCardNumberErrorText }}</span>
                  </p>
                </label>

                <label for="">
                  <p v-show="dateIsValid">
                    <span><b>{{ cardMonth ? cardMonth : '' }}</b> {{ getCardPlaceholders('cardMonth', 2) }} </span>
                    <span> / </span>
                    <span><b>{{ cardYear ? cardYear : '' }}</b> {{ getCardPlaceholders('cardYear', 2) }}</span>
                  </p>
                  <p v-show="!dateIsValid">
                    <span>{{ $t('check.card.date') }}</span>
                  </p>
                </label>
              </div>

              <div class="back">
                <label for="">
                  <p>
                    <span>
                      <b v-if="cvc">{{ cvc }}</b> {{ getCardPlaceholders('cvc', 3) }}
                    </span>
                  </p>
                  <p v-show="false">
                    <span>{{ $t('check.card.cvc') }}</span>
                  </p>
                </label>
              </div>
            </div>
          </div>

          <div class="holder">
            <transaction-info-card
              :amount="TRANSACTION.amount.amount"
              :comission="TRANSACTION.amount.commission"
              :currency="TRANSACTION.currency"
              :payment-systems="PROJECT.provider.paymentSystems"
              @back-away="tryAgain"
            />
          </div>

          <transition name="fade">
            <div class="helper" v-if="IS_IOS && iosHelperShow">
              <p>
                {{ $t('to.pay') }}
                <span v-text="displayAmount"></span>
              </p>
              <p>{{ $t('how.pay') }}</p>
              <button @click.prevent="iosHelperShow = false">{{ $t('with.apple.pay') }}</button>
              <button @click.prevent="iosHelperShow = false">{{ $t('with.card') }}</button>
            </div>
          </transition>
        </div>

        <div class="card-fields-container">
          <form @submit.prevent="goPay">
            <fieldset ref="cardFieldset" :class="{ 'focused': IS_MOBILE_ONLY && someInputFocused }">
              <InputCardPan
                ref="panElement"
                v-model="cardPan"
                :input-id="getElementsId[0]"
                @focusNext="$refs.androidBtnsManager.next($event)"
              />

              <InputCardDate
                ref="dateElement"
                :input-id-month="getElementsId[1]"
                :input-id-year="getElementsId[2]"
                :class="{ 'uzbekistan': isPrimaryUzbekistanProvider }"
                @onMonth="cardMonth = $event"
                @onYear="cardYear = $event"
                @focusPrev="$refs.androidBtnsManager.prev()"
                @focusNext="$refs.androidBtnsManager.next($event)"
              />

              <InputCVC
                v-show="isShownSecurityCode"
                ref="CVCElement"
                v-model="cvc"
                :input-id="getElementsId[3]"
                @CVCFocused="CVCFocused"
                @getHiddenValue="getHiddenCVC"
                @focusPrev="$refs.androidBtnsManager.prev()"
              />

              <!-- TODO: hidden whith in CPME-3196 -->
              <div v-if="IS_MOBILE_ONLY && someInputFocused" :class="{ 'uzbekistan': isPrimaryUzbekistanProvider }">
                <p
                  v-html="displayPolicyPrivacy"
                  :class="{ 'subscription': isSubscription }"
                ></p>
              </div>
            </fieldset>

            <button
              :disabled='!isValid'
              :class="{
                'fixed': IS_MOBILE_ONLY && isValid,
              }"
              @click.prevent="goPay"
              v-text="displayAmountButton"
            ></button>
          </form>

          <AndroidBtnsManager
            ref="androidBtnsManager"
            v-show="IS_ANDROID && IS_MOBILE_ONLY && someInputFocused"
            :elementsId="getElementsId"
            :currentElement="currentElement"
          />

          <div v-if="(IS_MOBILE_ONLY && !someInputFocused) || !IS_MOBILE_ONLY" class="policy-privacy" v-html="displayPolicyPrivacy"></div>
        </div>
      </div>

      <footer-section />
    </div>
  </cs-preloader-overlay>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex';

import { formatAmount } from 'lib/utils/amount';

import HTTPService from '@/services/Http';
import { tryAgain } from '@/services/mixins';
import { getPayterraPaymentData } from 'services/payterra';

import TransactionInfoCard from 'molecules/transaction/TransactionInfoCard';
import PaymentHeader from 'organisms/payment/PaymentHeader';

import { EXTERNAL_CHECKOUT_SHOP_ID } from '@/.env';

const CARD_CLASSES = {
  visa: 'visa',
  mastercard: 'mastercard',
  mir: 'mir',
  maestro: 'maestrocard',
  'american-express': 'american-express',
  jcb: 'jcb',
  'diners-club': 'diners-club',
  humo: 'humo',
  uzcard: 'uzcard',
  default: '',
};

const BANK_NAMES = {
  sberbank: 'sberbank', // Сбербанк
  tinkoff: 'tinkoff', // Тинькофф
  alfabank: 'alfa-bank', // Альфа-банк
  sovcombank: 'SOVCOMBANK', // Совкомбанк // Sovcombank
  cetelem: 'CETELEM', // Сетелем // Cetelem
  'saint-petersburg-bank': 'PETERSBURG', // Банк Санкт-Петербург // Bank Saint Petersburg
  'pochta-bank': 'POCHTA', // Почта Банк // Pochta Bank
  'absolut-bank': 'ABSOLUT', // Абсолют Банк // Absolut Bank
  'unicredit-bank': 'UNICREDIT', // ЮниКредит Банк // UniCredit Bank Russia
  'credit-europe-bank': 'EUROPE', // Кредит Европа Банк // Credit Europe Bank
  'avangard-bank': 'AVANGARD', // Банк Авангард // Avangard
  'uralsib-bank': 'URALSIB', // Банк Уралсиб // Uralsib ATM
  'home-credit-bank': 'HOME', // Хоум Кредит Банк // Home Credit & Finance Bank
  'vozrozhdenie-bank': 'VOZROZHDENIYE', // Возрождение Банк // Vozrozhdenie Bank
  'center-invest-bank': 'CENTER-INVEST', // Центр-инвест Банк // Center-invest Bank
  'yandex-money': 'YANDEX', // Яндекс.Деньги // PS Yandex.Money
  'modul-bank': 'MODUL', // МодульБанк // Modul Bank
  'dom-rf-bank': 'ROSSIYSKY', // ДОМ.РФ Банк // Rossiysky Capital
  rosbank: 'ROSBANK', // Росбанк // Rosbank
  talkbank: 'TALKBANK', // TalkBank // Talkbank
  'russian-agricultural-bank': 'AGRICULTURAL', // РоссельхозБанк // Russian Agricultural Bank
  'russian-standard-bank': 'RUSSKIY', // Русский Стандарт Банк // Russian Standard Bank
  'vtb-bank': 'VTB', // ВТБ // VTB Bank
  'otkritie-bank': 'OTKRITIE', // Открытие Банк // Otkritie
  'tochka-bank': 'TOCHKA', // Точка Банк // Tochka Bank
  'renaissance-credit': 'RENAISSANCE', // Ренессанс Кредит Банк // Renaissance Credit
  'moscow-credit-bank': 'MOSCOW', // Московский Кредитный Банк // Credit Bank of Moscow
  'orient-express-bank': 'ORIENT', // Восточный Банк // Orient Express Bank
  'mts-bank': 'MTS', // МТС Банк // Public Joint-Stock Company MTS Bank
  promsvyazbank: 'PROMSVYAZBANK', // ПромсвязьБанк // Promsvyazbank
  raiffeisenbank: 'RAIFFEISENBANK', // Райффайзенбанк // Raiffeisenbank
};

const UZB_CARD_PATTERNS = [8600, 6262, 5614, 9860];

export default {
  name: 'EnterCard',
  mixins: [
    tryAgain,
  ],
  components: {
    TransactionInfoCard,
    PaymentHeader,
  },
  created() {
    this.loadData();
  },
  mounted() {
    this.isMounted = true;

    this.addEventListeners();
    this.$watch('$refs.panElement.creditCardType', value => {
      this.classBankType = CARD_CLASSES[value.type || 'default'];
    });
  },
  data() {
    return {
      isMounted: false,
      loading: false,

      // form vars
      cardPan: '',
      cardMonth: '',
      cardYear: '',
      cvc: '',
      hiddenCvc: '',

      // card
      classBankType: '',
      classBankName: '',
      currentElement: 'panInput',

      // states
      flipped: false,
      iosHelperShow: false,
      someInputFocused: false,

      paymentToken: null,
    };
  },
  computed: {
    displayPolicyPrivacy() {
      if (this.isSubscription) {
        return this.$t('offerta.link.subscription', {
          offerta: this.getOfferta,
        });
      }

      return this.$t('offerta.link.base', {
        offerta: this.getOfferta,
      });
    },
    displayTitle() {
      if (this.isSubscription) {
        return this.$t('common.subscribing');
      }

      return this.$t('transaction.from.card');
    },
    panIsValidClass() {
      return this.isMounted && this.$refs.panElement.isValid;
    },
    panIsValid() {
      return this.isMounted && this.$refs.panElement.cardIsValid;
    },
    dateIsValid() {
      return this.isMounted && !this.$refs.dateElement.$v.$error;
    },
    // eslint-disable-next-line complexity
    isValid() {
      if (!this.isMounted) {
        return false;
      }

      if (!this.panIsValid || !this.dateIsValid) {
        return false;
      }

      if (!this.cardPan || !this.cardMonth || !this.cardYear) {
        return false;
      }

      if (this.isShownSecurityCode) {
        return this.$validate.isNotEmptyString(this.cvc) && !this.$refs.CVCElement.$v.$error;
      }

      return true;
    },
    inputActive() {
      return this.cardPan.length || this.cardMonth.length || this.cardYear.length || this.cvc.length;
    },
    isSubscription() {
      const { billTariff } = this.TRANSACTION;

      if (this.$validate.isNull(billTariff)) {
        return false;
      }

      if (!this.$validate.isObjectWithFields(billTariff, ['subscription'])) {
        return false;
      }

      if (this.$validate.isNull(billTariff.subscription)) {
        return false;
      }

      return true;
    },
    displayAmount() {
      const { amount, currency } = this.TRANSACTION;

      return formatAmount(amount.amount + amount.commission, currency);
    },
    displayAmountButton() {
      const { amount, currency } = this.TRANSACTION;

      if (this.isSubscription) {
        return `${this.$t('common.pay')} ${formatAmount(amount.amount + amount.commission, currency)}`;
      }

      return `${this.$t('common.transfer')} ${formatAmount(amount.amount + amount.commission, currency)}`;
    },
    formattedCardPan() {
      if (!this.isMounted) {
        return null;
      }

      return this.cardPan.replace(/\s/g, '');
    },
    getElementsId() {
      return [
        'panInput',
        'input-card-date-month',
        'input-card-date-year',
        this.isShownSecurityCode && 'input-card-security-code',
      ].filter(i => i);
    },
    isShownSecurityCode() {
      if (this.isPrimaryUzbekistanProvider) {
        return false;
      }

      if (this.$validate.isNull(this.formattedCardPan)) {
        return true;
      }

      const findedCardByPattern = UZB_CARD_PATTERNS
        .find(pattern => this.formattedCardPan.startsWith(pattern));

      return this.$validate.isUndefined(findedCardByPattern);
    },
    displayCardNumberErrorText() {
      if (this.isMounted) {
        const { creditCardType } = this.$refs.panElement;

        if (this.$validate.isEmptyObject(creditCardType)) {
          return this.$t('check.card.pan');
        }

        if (this.getAvailablePaymentSystems.includes(creditCardType.niceType.toUpperCase())) {
          return this.$t('check.card.pan');
        }

        return this.$t('available.payment.systems.error', {
          paymentSystems: this.getAvailablePaymentSystems.join(', '),
        });
      }

      return this.$t('check.card.pan');
    },
    ...mapState([
      'PROJECT',
      'COMMISSION',
      'TRANSACTION',
      'TRANSACTION_KEY',
      'IS_IOS',
      'IS_ANDROID',
      'IS_MOBILE_ONLY',
      'PAYMENT_TYPE',
    ]),
    ...mapGetters([
      'isExternalCheckoutProvider',
      'isPrimaryUzbekistanProvider',
      'getOfferta',
      'getLanguage',
      'isСheckoutPay',
      'getShopKey',
      'getAvailablePaymentSystems',
      'isDigitalContent',
      'isPaymentAutoRedirection',
      'getSuccessUrl',
      'getFailUrl',
    ]),
  },
  watch: {
    cardPan(val) {
      const cardFormatted = val.replace(/\s*/g, '');

      if (cardFormatted.length < 6) {
        this.classBankName = '';
      } else {
        HTTPService.getBankName(cardFormatted.slice(0, 6))
          .then(({ data }) => {
            if (!data.bankName) {
              return;
            }

            for (const name in BANK_NAMES) {
              if (data.bankName.toLowerCase().indexOf(BANK_NAMES[name].toLowerCase()) > -1) {
                this.classBankName = name;
                return;
              }
            }
          });
      }
    },
    someInputFocused(val) {
      if (!this.IS_MOBILE_ONLY) {
        return;
      }

      if (val) {
        document.getElementById('root').classList.add('focused');
      } else {
        document.getElementById('root').classList.remove('focused');
      }
    },
    isShownSecurityCode(val) {
      if (!val) {
        this.cvc = '';
      }
    },
  },
  methods: {
    async loadData() {
      const { billTariff } = this.TRANSACTION;

      const payload = this.$validate.isNull(billTariff)
        ? { projectCode: this.PROJECT.project_code }
        : { billCode: this.TRANSACTION_KEY };

      await this.GET_PROJECT_LOCALE(payload);
    },
    async goPay() {
      if (!this.isValid) {
        return false;
      }

      this.loading = true;
      /*
      if (this.$validate.isNotEmptyString(this.TRANSACTION_KEY)) {
        await this.GET_BILL(this.TRANSACTION_KEY)
          .then(() => {
            if (!this.PROJECT.status || this.PROJECT.status === 'INACTIVE') {
              this.$router.push({ name: 'project.blocked' });
              return;
            }
          })
          .catch(error => {
            if (error.status === 403) {
              this.SAVE_BILL(error.data);
              this.$router.push({ name: 'bill.blocked' })
            } else {
              this.$router.push({ name: 'service.error' });
            }
          });

        if (this.PROJECT.bill_status === 'CLOSED') {
          this.$router.push({ name: 'bill.payed' });
          return;
        }
      } */

      const cardPan = this.cardPan.replace(/\s*/g, '');

      const requestData = {
        cardData: {
          pan: cardPan,
          month: this.cardMonth,
          year: this.cardYear,
          cvv: this.hiddenCvc,
        },
        paymentId: this.TRANSACTION.id,
      };

      if (this.TRANSACTION.billId) {
        requestData.billId = this.TRANSACTION.billId;
      }

      if (this.isDigitalContent) {
        sessionStorage.setItem('passwordCard', cardPan.slice(-4));
      }

      if (this.isSubscription) {
        sessionStorage.setItem('paymentId', this.TRANSACTION.id);
        sessionStorage.setItem('passwordCard', cardPan.slice(-4));
        sessionStorage.setItem('accessToken', this.TRANSACTION.accessToken);
      }

      if (this.isExternalCheckoutProvider) {
        // eslint-disable-next-line no-undef
        const checkout = YooMoneyCheckout(EXTERNAL_CHECKOUT_SHOP_ID);

        const response = await checkout.tokenize({
          number: cardPan,
          cvc: this.hiddenCvc,
          month: this.cardMonth,
          year: this.cardYear,
        });

        console.log('tokenize:response', response);
        if (response.status === 'success') {
          const { paymentToken } = response.data.response;

          requestData.cardData.token = paymentToken;
        }
      }

      let jsPayData;

      if (this.isСheckoutPay) {
        try {
          jsPayData = await getPayterraPaymentData({
            card_data: {
              card_number: cardPan,
              card_month: this.cardMonth,
              card_year: this.cardYear,
              cvv: this.isPrimaryUzbekistanProvider ? '000' : this.hiddenCvc,
            },
            shop_key: this.getShopKey,
          }, this.TRANSACTION.id);
        } catch (error) {
          console.log(error);

          this.$router.push({ name: 'failed.payment' });
          return;
        }

        HTTPService.checkoutSale({
          paymentId: this.TRANSACTION.id,
          ...jsPayData,
        })
          .then(response => {
            const data = response.data;
            const status = data.status;

            if (this.$validate.isNotEmptyString(data.confirmationUrl)) {
              window.location.assign(data.confirmationUrl);
              return;
            }

            if (status === 'WAIT_OTP_VERIFICATION') {
              this.$router.push({ name: 'confirmation.sms' });
              return;
            }

            /*
              TODO: @danealton не могу проверить статус платежа из страницы ввода кода,
              поскольку там вся механика происходит на странице кассы по урл из response.data.action
            */
            if (data.enrolled) {
              if (this.$validate.isEmptyString(data.action)) {
                this.$router.push({ name: 'service.error' });
                return;
              }

              this.$router.push({ name: 'ThreeDSecure', params: { data } });
              return;
            }

            if (status === 'NO_TICKETS_LEFT') {
              this.$router.push({ name: 'no.tickets.left' });
              return;
            }

            if (status === 'FAIL') {
              if (this.isPaymentAutoRedirection) {
                window.location.assign(this.getFailUrl);
                return;
              }

              this.$router.push({ name: 'failed.payment' });
              return;
            }

            if (status === 'FRAUD_DECLINE') {
              if (this.isPaymentAutoRedirection) {
                window.location.assign(this.getFailUrl);
                return;
              }

              this.$router.push({ name: 'rejected.payment' });
              return;
            }

            if (status === 'SUCCESS') {
              this.$router.push({ name: 'payment.origin' });
            }
          })
          .catch(error => {
            const fraudDeclined = error.data && error.data.status && error.data.status === 'FRAUD_DECLINE';

            if (error.status === 400 && error.data.status === 'TRANSACTION_LIMIT_EXCEEDED') {
              this.$alert({
                type: 'danger',
                title: 'error.transition.card',
              });
              return;
            }

            if (this.isPaymentAutoRedirection) {
              window.location.assign(this.getFailUrl);
              return;
            }

            if (error.status === 400 && fraudDeclined) {
              this.$router.push({ name: 'rejected.payment' });
              return;
            }

            if (error.status === 400) {
              this.$router.push({ name: 'failed.payment' });
              return;
            }

            this.$router.push({ name: 'service.error' });
          })
          .finally(() => {
            this.loading = false;
          });

        return;
      }

      HTTPService.cardSale(requestData)
        .then(response => {
          const data = response.data;
          const status = data.status;

          if (this.$validate.isNotEmptyString(data.confirmationUrl)) {
            window.location.assign(data.confirmationUrl);
            return;
          }

          if (status === 'WAIT_OTP_VERIFICATION') {
            this.$router.push({ name: 'confirmation.sms' });
            return;
          }

          /*
            TODO: @danealton не могу проверить статус платежа из страницы ввода кода,
            поскольку там вся механика происходит на странице кассы по урл из response.data.action
          */
          if (data.enrolled) {
            if (this.$validate.isEmptyString(data.action)) {
              this.$router.push({ name: 'service.error' });
              return;
            }

            this.$router.push({ name: 'ThreeDSecure', params: { data } });
            return;
          }

          if (status === 'NO_TICKETS_LEFT') {
            this.$router.push({ name: 'no.tickets.left' });
            return;
          }

          if (status === 'FAIL') {
            if (this.isPaymentAutoRedirection) {
              window.location.assign(this.getFailUrl);
              return;
            }

            this.$router.push({ name: 'failed.payment' });
            return;
          }

          if (status === 'FRAUD_DECLINE') {
            if (this.isPaymentAutoRedirection) {
              window.location.assign(this.getFailUrl);
              return;
            }

            this.$router.push({ name: 'rejected.payment' });
            return;
          }

          if (status === 'SUCCESS') {
            this.$router.push({ name: 'payment.origin' });
          }
        })
        .catch(error => {
          const fraudDeclined = error.data && error.data.status && error.data.status === 'FRAUD_DECLINE';

          if (error.status === 400) {
            if (error.data.status === 'NO_TICKETS_LEFT') {
              this.$router.push({ name: 'no.tickets.left' });
              return;
            }

            if (error.data.status === 'TRANSACTION_LIMIT_EXCEEDED') {
              this.$alert({
                type: 'danger',
                title: 'error.transition.card',
              });
              return;
            }
          }

          if (this.isPaymentAutoRedirection) {
            window.location.assign(this.getFailUrl);
            return;
          }

          if (error.status === 400 && fraudDeclined) {
            this.$router.push({ name: 'rejected.payment' });
            return;
          }

          if (error.status === 400) {
            this.$router.push({ name: 'failed.payment' });
            return;
          }

          this.$router.push({ name: 'service.error' });
        })
        .finally(() => {
          this.loading = false;
        });
    },
    onMonth(value) {
      this.cardMonth = value;
    },
    onYear(value) {
      this.cardYear = value;
    },
    CVCFocused(value) {
      this.flipped = value;
    },
    getCardPlaceholders(state, maxCount) {
      let c = maxCount - this[state].length;
      const l = state === 'cvc' ? '*' : 'x';
      if (!c || c < 0) c = 0;
      return l.repeat(c);
    },
    inputOnFocus(e) {
      this.$refs.androidBtnsManager.setCurrentIndex(e.target.id);
      this.someInputFocused = true;
    },
    inputOnBlur(e) {
      if ((e.relatedTarget && e.relatedTarget.dataset.obblur === 'prevent') || (e.relatedTarget && e.relatedTarget.nodeName && e.relatedTarget.nodeName === 'INPUT')) return;
      this.someInputFocused = false;
    },
    getHiddenCVC(value) {
      this.hiddenCvc = value;
    },
    addEventListeners() {
      Array.prototype.forEach.call(this.getElementsId, el => {
        document.getElementById(el).addEventListener('focus', this.inputOnFocus.bind(this));
        document.getElementById(el).addEventListener('blur', this.inputOnBlur.bind(this));
      });

      !this.IS_IOS && document.getElementById(this.$refs.androidBtnsManager.getElementByIndex()).focus();
    },
    removeEventListeners() {
      Array.prototype.forEach.call(this.getElementsId, el => {
        document.getElementById(el).removeEventListener('focus', this.inputOnFocus.bind(this));
        document.getElementById(el).removeEventListener('blur', this.inputOnBlur.bind(this));
      });
    },
    ...mapActions(['GET_BILL', 'SAVE_BILL', 'GET_PROJECT_LOCALE']),
  },
};
</script>

<style lang="sass" scoped>
  .information-card-container
    margin: 15px auto 15px
    max-width: $critical-width

    @include breakpoint(small)
      margin: 30px auto 30px

    @include breakpoint(medium)
      margin: 50px auto 30px

  .holder

    &:before
      @include pseudo-block
      opacity: 0
      left: -50vw
      right: -50vw
      z-index: -1
      transition: opacity .448s ease-in-out
      transition-delay: .224s
      top: -10rem
      bottom: calc(-1.28rem + -3rem)

      @include breakpoint(cramped)
        bottom: calc(-1.88rem + -3.35rem)

      @include breakpoint(medium)
        bottom: calc(-2.24rem + -2.8rem)

  .policy-privacy
    margin-top: 20px
    padding: 0
    font-weight: 400
    font-size: 12px
    line-height: 17px
    color: #212121

    &::v-deep
      a
        display: inline
        color: #9B51E0
        word-break: break-word
</style>
